import type { PortalOrder, PortalUser, TenantRole, ValidationStatus } from '@provalia/shared';
import { createApi } from '../shared/api';

/** Una sola instancia: el token del portal vive con su propia clave. */
export const api = createApi('portal');

// ── Formas de las respuestas de /api/portal/* ─────────────────────────────

export interface PortalInfo {
  name: string;
  accentColor: string;
  hasLogo: boolean;
  available: boolean;
}

export interface PortalSession {
  scope: string;
  sub: string;
  email: string;
  name: string;
  role: TenantRole;
  cardCode: string | null;
  viewAs?: ViewAs;
}

/** Mirando el portal como un proveedor (solo lectura). */
export interface ViewAs {
  realRole: TenantRole;
  realCardCode: string | null;
  supplierName: string;
}

export interface Me {
  session: PortalSession;
  tenant: { slug: string; name: string; accentColor: string; hasLogo: boolean };
  rules: { priceTolerancePct: number; dateToleranceDays: number };
  supplier: { cardCode: string; name: string; hasLogo: boolean } | null;
  /** Puesto si se está mirando como un proveedor. */
  viewAs: ViewAs | null;
  /** Este usuario puede usar «Ver como proveedor». */
  canViewAs: boolean;
}

export interface OrdersResponse {
  orders: PortalOrder[];
  at: string;
  /** Proveedores dados de alta y con acceso en el portal. */
  suppliersEnabled?: number;
}

export interface ActivityRecord {
  id: number;
  at: string;
  actor: string;
  actorRole: string;
  cardCode: string | null;
  docEntry: number | null;
  lineNum: number | null;
  action: string;
  detail: Record<string, unknown>;
}

export interface DeliveryNoteRecord {
  id: string;
  number: string;
  description: string | null;
  fileName: string | null;
  createdBy: string;
  createdAt: string;
  lines: Array<{
    lineNum: number;
    price: number | string;
    orderPrice: number | string;
    deviationPct: number | string | null;
    status: ValidationStatus;
  }>;
}

export interface OrderDetail {
  order: PortalOrder;
  activity: ActivityRecord[];
  deliveryNotes: DeliveryNoteRecord[];
}

export interface DeliveryNoteResult {
  accepted: number[];
  pendingReview: number[];
  attachmentEntry: number | null;
}

export interface SupplierRow {
  cardCode: string;
  name: string;
  email: string | null;
  enabled: boolean;
  evalManual: boolean;
  hasLogo: boolean;
  createdAt: string;
  users: number;
  openOrders: number;
}

export interface SapSupplier {
  cardCode: string;
  name: string;
  email: string | null;
}

export interface OutboxMessage {
  id: string | number;
  createdAt: string;
  kind: string;
  to: string[];
  subject: string;
  status: 'pending' | 'sent' | 'failed' | 'skipped' | string;
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
}

export interface SyncReport {
  at: string;
  ok: boolean;
  error: string | null;
  openOrders: number;
  published: number;
  modified: number;
  reminders: number;
  closed: number;
  digests: number;
  mail: { sent: number; failed: number };
  silent: boolean;
}

export type { PortalUser };

/** Postgres devuelve NUMERIC como texto: se normaliza antes de pintar. */
export function toNum(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
